import {Alert, Attributes, AttributesRow} from 'platform/components';
import {GridItem, Text, Grid, Space, Box, Hide} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';
import styled from 'styled-components';

import {useField} from 'react-final-form';
import {useSelector} from 'react-redux';

import i18n from '@dms/i18n';
import {EMPTY_PLACEHOLDER} from '@dms/shared';

import {parseDate, suffixTestId, TestIdProps} from 'shared/web';

import {selectAudit, selectTestDrive} from '../../../store/carAudit/selectors';
import {selectUserSelectedLanguage} from '../../../store/user/selectors';
import {AuditParamDefinition} from '../../../types/AuditParamDefinition';
import {TConditionFormValue} from '../../../types/ConditionTypes';
import {getNameAccordingToLocale} from '../../../utils/getNameAccordingToLocale';
import {AuditParamType} from '../types/AuditParamType';
import {findByParamType} from '../utils/findByParamType';
import {getFormFieldName} from '../utils/getFormFieldName';
import {AuditFormField} from './AuditFormField';
import {CarAuditAppBanner} from './CarAuditAppBanner';
import {CategoryGeneralComment} from './CategoryGeneralComment';

const AlertWrapper = styled.div`
  margin-bottom: 16px;
`;

const timeParams: string[] = [AuditParamType.START_TIME, AuditParamType.END_TIME];

export const TestDrive = (props: TestIdProps) => {
  const locale = useSelector(selectUserSelectedLanguage);
  const testDriveStructure = useSelector(selectTestDrive);
  const audit = useSelector(selectAudit);
  const formatDateTime = useDateTimeFormatter();

  const commentParamDefinition = findByParamType(
    testDriveStructure?.paramDefinitions?.additionalInformation,
    AuditParamType.ADDITIONAL_COMMENT
  );

  const rowsParam: AttributesRow[] = [];

  let dateValue;
  let isGpsSignalWeak = false;
  let isNotTested = true;

  const getRowValue = (paramDefinition: AuditParamDefinition, fieldValue?: TConditionFormValue) => {
    // Handle toggle/boolean fields
    if (paramDefinition.type === AuditParamType.TOGGLE) {
      if (fieldValue === null || fieldValue === undefined) {
        return EMPTY_PLACEHOLDER;
      }
      return Boolean(fieldValue) ? i18n.t('general.labels.yes') : i18n.t('general.labels.no');
    }

    if (!Boolean(fieldValue)) {
      return EMPTY_PLACEHOLDER;
    }

    if (Boolean(fieldValue) && timeParams.includes(paramDefinition.type)) {
      return formatDateTime('timeShort', parseDate(String(fieldValue)));
    }

    if (paramDefinition.unit) {
      return `${fieldValue} ${paramDefinition.unit}`;
    }

    return String(fieldValue);
  };

  const toggleParamDefinition = findByParamType(
    testDriveStructure?.paramDefinitions?.relatedActions,
    AuditParamType.TOGGLE
  );

  testDriveStructure?.paramDefinitions?.relatedActions?.forEach((paramDefinition) => {
    if (
      paramDefinition.type === AuditParamType.CATEGORY_STATE ||
      paramDefinition.type === AuditParamType.TOGGLE
    ) {
      return;
    }

    const fieldName = getFormFieldName(testDriveStructure.id, paramDefinition.id);
    const fieldValue = audit?.fields?.[fieldName];

    if (paramDefinition.type === AuditParamType.TEST_DRIVE_DATE) {
      if (Boolean(fieldValue) && fieldValue !== '') {
        dateValue = formatDateTime('dateShort', parseDate(String(fieldValue)));
      }

      return;
    }

    if (paramDefinition.type === AuditParamType.WEAK_GPS_SIGNAL) {
      isGpsSignalWeak = Boolean(fieldValue);
      return;
    }

    if (Boolean(fieldValue) && isNotTested) {
      isNotTested = false;
    }

    rowsParam.push({
      label: paramDefinition.label ? getNameAccordingToLocale(locale, paramDefinition.label) : '',
      value: getRowValue(paramDefinition, fieldValue),
    });
  });

  const toggleFieldName = toggleParamDefinition
    ? getFormFieldName(testDriveStructure?.id ?? '', toggleParamDefinition.id)
    : undefined;

  const toggleField = useField(toggleFieldName ?? '', {subscription: {value: true}});
  const isTestDrivePerformedToggled = Boolean(toggleField.input.value);

  return (
    <>
      {isGpsSignalWeak && (
        <AlertWrapper data-testid={suffixTestId('alertWrapper', props)}>
          <Alert
            data-testid={suffixTestId('warning', props)}
            type="inline"
            variant="warning"
            title={i18n.t('entity.condition.notifications.finishedWithWarnings')}
            message={i18n.t('entity.condition.notifications.lowGpsSignal')}
          />
        </AlertWrapper>
      )}
      {toggleParamDefinition && testDriveStructure && (
        <Box width="fit-content">
          <AuditFormField
            paramDefinition={toggleParamDefinition}
            categoryId={testDriveStructure.id}
            uniqueKey={testDriveStructure.uniqueKey}
            data-testid={suffixTestId('testDrivePerformedToggle', props)}
          />
          <Space vertical={4} />
        </Box>
      )}
      <Grid columns={2}>
        <GridItem>
          <Hide when={isTestDrivePerformedToggled}>
            {isNotTested ? (
              <Text
                alternative
                color="danger"
                data-testid={suffixTestId('vehicleNotTested', props)}
              >
                {i18n.t('entity.condition.labels.vehicleNotTested')}
              </Text>
            ) : (
              <Text alternative data-testid={suffixTestId('testDate', props)}>
                {dateValue}
              </Text>
            )}
          </Hide>
          <Space vertical={2} />
          <Attributes rows={rowsParam} data-testid={props['data-testid']} />
        </GridItem>
        <GridItem>
          <CarAuditAppBanner />
        </GridItem>
      </Grid>
      <CategoryGeneralComment
        category={testDriveStructure}
        paramDefinitionId={commentParamDefinition?.id}
      />
    </>
  );
};
